/**
 * Spend & Save tiers, sourced from the `spend_and_save` metaobject via GM_STATE.
 *
 * The mini cart (mini-cart.js) and the cart page (spend-and-save.liquid) both resolve a cart
 * total through this same set of rules, so the two can never disagree about which tier is live.
 */

/**
 * A usable threshold. Guards null and '' explicitly — both coerce to 0, which would otherwise
 * read as a tier the cart has already cleared and render a success message out of nowhere.
 */
const isThreshold = (value) => value !== null && value !== '' && Number.isFinite(Number(value))

/**
 * The tiers a country is eligible for, ordered by threshold.
 *
 * A tier with no `availableCountries` shows everywhere. Tiers arrive unsorted from
 * parse-spend-and-save.liquid — `sort_natural` stringifies `minThreshold`, so 10000 would order
 * before 5000 — which is why the numeric sort happens here.
 *
 * @param {object[]} tiers Metaobject tiers.
 * @param {string} [country] Active `localization.country.iso_code`.
 * @returns {object[]}
 */
export const availableTiers = (tiers, country) =>
  (Array.isArray(tiers) ? tiers : [])
    .filter((tier) => isThreshold(tier?.minThreshold))
    .filter((tier) => !tier.availableCountries?.length || !country || tier.availableCountries.includes(country))
    .sort((a, b) => Number(a.minThreshold) - Number(b.minThreshold))

/**
 * The tier to display for a cart total, or null when no tier applies.
 *
 * Below the highest threshold the next *unreached* tier is shown, so the customer always sees
 * what they are working towards; the comparison is strict, so landing exactly on a threshold
 * clears it. Past the highest tier there is nothing left to reach and the highest tier's success
 * state is shown instead.
 *
 * @param {number} cartTotal Cart total in cents — `cart.total_price`, post-discount.
 * @param {object[]} tiers Metaobject tiers.
 * @param {string} [country] Active `localization.country.iso_code`.
 * @returns {{tier: object, goalReached: boolean, remaining: number, percentage: number}|null}
 */
export const matchSpendAndSaveTier = (cartTotal, tiers, country) => {
  const sorted = availableTiers(tiers, country)
  if (!sorted.length) return null

  const total = Number(cartTotal) || 0
  const nextTier = sorted.find((candidate) => total < Number(candidate.minThreshold))
  const tier = nextTier ?? sorted[sorted.length - 1]
  const threshold = Number(tier.minThreshold)

  return {
    tier,
    goalReached: !nextTier,
    remaining: Math.max(0, threshold - total),
    percentage: threshold > 0 ? Math.min(100, Math.floor((total / threshold) * 100)) : 100
  }
}

/**
 * Renders a tier message to HTML.
 *
 * Two tokens, both merchandising-facing: `[money]` is the amount still to spend, and
 * `[bold]…[/bold]` marks the reward itself — the "what" the design always sets in bold. Tokens
 * rather than HTML because the cart page's Liquid runs `strip_html`, so a raw <b> typed into the
 * metaobject would bold in the mini cart and silently vanish on the cart page.
 *
 * Everything else is stripped for the same reason: the two surfaces have to agree, and the result
 * is injected with dangerouslySetInnerHTML. Only a matched pair converts, so an unclosed token is
 * dropped instead of bleeding bold through the rest of the panel.
 *
 * @param {string} message Raw metaobject message.
 * @param {string} money Pre-formatted amount, markup included.
 * @returns {string} HTML.
 */
export const formatSpendAndSaveMessage = (message, money) =>
  String(message ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\[money\]/g, money)
    .replace(/\[bold\]([\s\S]*?)\[\/bold\]/g, '<b>$1</b>')
    .replace(/\[\/?bold\]/g, '')
